import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import type { DP } from '../../../drupal/blockDefinitions'
import { featuredContentSrcSet } from '../../../drupal/utils/imageSizes'
import { parseDrupalImageUrls } from '../../../drupal/utils/modernImage'
import { dummyData, randomTitle, randomWords } from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import type { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { fcBlockClasses, FeaturedContentBlockLayout } from './featuredContentBlock.layout'
import type { FeaturedContentBlockProps } from './featuredContentBlock.state'

export const featuredContentBlock: ModernBlock<
  FeaturedContentBlockProps,
  any,
  any,
  DP.FeaturedContent
> = {
  kind: 'featuredContentBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    fromJSX((updateBlock) => (
      <FeaturedContentBlockLayout.Inner amountNested={amountNested!} imagePosition={blockProps.imageDirection}>
        <FeaturedContentBlockLayout.Content
          label={blockProps.label}
          title={blockProps.title}
          intro={blockProps.intro}
          showPrimary={blockProps.showPrimary}
          showSecondary={blockProps.showSecondary}
          primaryAction={blockProps.primaryAction!}
          secondaryAction={blockProps.secondaryAction!}
        />
        <FeaturedContentBlockLayout.Visual image={blockProps.image} imagePosition={blockProps.imageDirection} />
      </FeaturedContentBlockLayout.Inner>
    )),
  blockClassName: (props) => `
    ${getClassNameModern(fcBlockClasses, 'block', 'mb-6')}
    ${getClassNameModern(fcBlockClasses, props.imageDirection == 'left' ? 'blockLeft' : 'blockRight', '')}
    ${props.bgColour ? FeaturedContentBlockLayout.ColorSchemeClassName(props.bgColour) : 'bg-light'}
  `,
  inView: {
    showAs: 'section',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(fcBlockClasses, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState,
      }
    },
  },
  adapters: {
    drupal: (dpBlock: DP.FeaturedContent, blockIndex: number) => {
      const targetId = dpBlock.target.type[0].target_id
      if (targetId != 'featured_content') return undefined
      const featuredContent = dpBlock.target.field_featured_content[0]

      if (!featuredContent.target) return undefined
      if (!featuredContent.target.field_title[0]) return undefined
      if (!featuredContent.target.field_image[0]) return undefined
      if (!featuredContent.target.field_feature_introduction[0]) return undefined
      if (!featuredContent.target.field_featured_link_label[0]) return undefined

      const fieldMediaImage = featuredContent.target.field_image[0].target.field_media_image[0]
      const fieldMediaSource = featuredContent.target.field_image[0]?.target.field_source[0]
        ? featuredContent.target.field_image[0]?.target.field_source
        : undefined
      const dpImages = fieldMediaImage
        ? parseDrupalImageUrls(fieldMediaImage, fieldMediaSource, featuredContentSrcSet)?.image
        : undefined

      return {
        anchorTitle: dpBlock.target.type[0]?.target_id,
        excludeFromPageMenu: dpBlock.target?.field_exclude_from_page_menu?.[0]?.value,
        imageDirection: dpBlock.target.field_featured_content_direction[0]?.value == 'image_left' ? 'left' : 'right',
        showBgColour: true,
        bgColour: 'lightred', // OVERWRITE INSTEAD OF `bgColour: dpBlock.target.field_featured_background_color ? dpBlock.target.field_featured_background_color[0] ? dpBlock.target.field_featured_background_color[0].value == 'lightblue' ? 'lightblue' : 'grey' : 'lightblue' : 'lightblue',`
        image: dpImages,
        label: featuredContent?.target.field_label[0]?.value,
        title: featuredContent?.target.field_title[0]?.value,
        intro: featuredContent?.target.field_feature_introduction[0]?.value,
        showPrimary: featuredContent?.target.field_featured_link_label[0]?.value ? true : false,
        showSecondary: dpBlock.target.field_link[0] ? true : false,
        primaryAction: linkWrapper({
          title: featuredContent?.target.field_featured_link_label[0]?.value,
          url: featuredContent?.url,
        }),
        secondaryAction: (dpBlock.target.field_link && dpBlock.target.field_link.length > 0)
          ? linkWrapper({
            title: dpBlock.target.field_link[0].title,
            url: dpBlock.target.field_link[0].processed_url,
            anchor: dpBlock.target?.field_link[0]?.options?.attributes?.id
          })
          : undefined,
      }
    },
  },
  seoEvents: (props, blockIndex) =>
    [
      props.showPrimary && props.primaryAction
        ? {
            id: `primary_button`,
            event: 'onClick',
            category: 'featured_content',
            label: window.location.pathname,
            action: 'primary_' + props.primaryAction.title,
          }
        : undefined,
      props.showSecondary && props.secondaryAction
        ? {
            id: `secondary_button`,
            event: 'onClick',
            category: 'featured_content',
            label: window.location.pathname,
            action: 'secondary_' + props.secondaryAction.title,
          }
        : undefined,
    ].filter((i) => i != undefined) as SeoEvent[],
  sampleExample: [
    {
      title: randomTitle(),
      anchorTitle: 'featured-sample-1',
      label: randomWords(),
      intro: dummyData.intro,
      showPrimary: true,
      showSecondary: true,
      imageDirection: 'left',
      showBgColour: true,
      bgColour: 'lightred',
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      image: dummyData.image,
    },
    {
      title: randomTitle(),
      anchorTitle: 'features-sample-2',
      label: randomTitle(),
      intro: dummyData.intro,
      showPrimary: false,
      showSecondary: false,
      imageDirection: 'right',
      showBgColour: false,
      bgColour: 'lightred',
      image: dummyData.image,
    },
  ],
}
